import { el, note, textBtn, type DesignerCtx } from './panels.js';
import { createColorBlock } from './controls.js';
import { run } from './commands.js';

/**
 * "Pozadina": one printed colour behind everything, or a photograph.
 *
 * The distinction matters enough to say out loud in the panel. The colour here
 * becomes ink on the product — it is not the workspace's backdrop, which lives
 * under ⚙ and changes nothing about the file. A photograph is not a background
 * at all in our model: it is an image layer filled to the zone and sent to the
 * back, which is what the button does in one step.
 */

export function renderBackgroundPanel(body: HTMLElement, ctx: DesignerCtx): () => void {
  body.append(note(ctx.texts.backgroundNote));

  const block = createColorBlock({
    ctx,
    title: ctx.texts.toolBackground,
    value: ctx.store.value().background,
    allowNone: true,
    onPick: (color) => run(ctx, { op: 'background', color: color ?? null }),
  });
  body.append(block.element);

  const photo = el('div', 'pzd-field');
  photo.append(note(ctx.texts.backgroundPhotoNote));
  photo.append(textBtn(ctx.texts.backgroundPhoto, () => {
    backgroundFromPhoto(ctx);
  }, { icon: 'image', className: 'pzd-command' }));
  body.append(photo);

  return () => block.destroy();
}

/**
 * The shopper's "put this picture behind everything": open the picture tool,
 * and once a picture is there fill the zone with it and drop it to the bottom.
 * Doing it in the Slike panel would need three deliberate steps in a row.
 */
function backgroundFromPhoto(ctx: DesignerCtx): void {
  const before = new Set(ctx.store.value().layers.map((layer) => layer.id));
  ctx.openTool('image');
  const stop = ctx.store.subscribe((state) => {
    const added = state.answer.layers.find((layer) => layer.kind === 'image' && !before.has(layer.id));
    if (!added) return;
    stop();
    run(ctx, { op: 'fitToZone', ids: [added.id], mode: 'fill' });
    run(ctx, { op: 'order', ids: [added.id], direction: 'back' });
  });
  // The subscription must not outlive the panel that made it: a shopper who
  // opened Pozadina, changed their mind and added a photo an hour later should
  // not have it yanked to the back.
  window.setTimeout(stop, 120000);
}
